import axios from 'axios'
import api from '@/api_alv'
import { router } from '@/router.js'

const DIR = 'publishing/usage'

/** Reads an error answer into { status, code, message, details }.
 *  The Usage API answers {error: {code, message, details}}; the login middleware in front of it still
 *  answers the legacy {error: 1, response: {errorMsg}}. A blob answer (a failed download) is read as JSON first.
 *  @param {Error} error axios error
 *  @return {Promise<{status: Number, code: String, message: String, details: Object}>} */
export async function readApiError(error) {
    const status = error.response ? error.response.status : 0
    let body = error.response ? error.response.data : null
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
        try { body = JSON.parse(await body.text()) } catch (e) { body = null }
    }
    if (body && body.error && typeof body.error === 'object') {
        return { status, code: body.error.code, message: body.error.message, details: body.error.details || {} }
    }
    const legacy = body && body.response ? body.response.errorMsg : null
    if (legacy === 'lang_ERROR_API_METHOD_LOGIN_FAILED') {
        localStorage.clear()
        router.push('/login')
    }
    return { status, code: legacy || 'network_error', message: legacy || error.message || '', details: {} }
}

/** One call to the Usage API.
 *  @return {Promise<{ok: true, status: Number, data: *, headers: Object}|{ok: false, error: Object}>} */
async function request(method, path, { params, data, responseType } = {}) {
    try {
        const response = await axios({
            method,
            url: api.rootUrl + DIR + path,
            params,
            data,
            responseType,
            headers: api.getConfig().headers
        })
        return { ok: true, status: response.status, data: response.data, headers: response.headers }
    } catch (error) {
        return { ok: false, error: await readApiError(error) }
    }
}

/** Drops empty filter values and joins lists with commas, as the Usage API reads them. */
export function cleanParams(params) {
    const out = {}
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return
        if (Array.isArray(value)) {
            if (value.length === 0) return
            out[key] = value.join(',')
            return
        }
        out[key] = value
    })
    return out
}

/** Saves a blob answer under the file name the API sent. */
export function saveDownload(result, fallbackName) {
    const disposition = (result.headers && result.headers['content-disposition']) || ''
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
    const plain = /filename="?([^";]+)"?/i.exec(disposition)
    const name = utf8 ? decodeURIComponent(utf8[1]) : (plain ? plain[1] : fallbackName)
    const url = window.URL.createObjectURL(new Blob([result.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', name)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

export default {
    // Datasets
    list: (params) => request('get', '', { params: cleanParams(params) }),
    detail: (id) => request('get', '/' + id),
    import: (formData) => request('put', '', { data: formData }),
    download: (id) => request('get', '/' + id + '/download', { responseType: 'blob' }),
    remove: (id) => request('delete', '/' + id),

    // Compare Usage and the review
    // noSheet: { fromStatement, airingLevel }, what to do with an unreported airing that has no cue sheet.
    // Left out, the backend repeats the previous run's choice.
    compare: (id, noSheet) => request('post', '/' + id + '/compare', noSheet ? { data: { noSheet } } : {}),
    findings: (id, params) => request('get', '/' + id + '/findings', { params: cleanParams(params) }),
    reviewFinding: (id, findingId, body) => request('post', '/' + id + '/findings/' + findingId + '/review', { data: body }),
    reviewFindings: (id, body) => request('post', '/' + id + '/findings/review', { data: body }),

    // Complaint export
    complaintPreview: (id, params) => request('get', '/' + id + '/complaints', { params: cleanParams(params) }),
    exportComplaints: (id, body) => request('post', '/' + id + '/complaints/export', { data: body, responseType: 'blob' }),

    // Stations
    datasetStations: (id) => request('get', '/' + id + '/stations'),
    stationList: (society) => request('get', '/stations', { params: cleanParams({ society }) }),
    stationAliases: () => request('get', '/station-aliases'),
    createStationAlias: (body) => request('post', '/station-aliases', { data: body }),
    updateStationAlias: (aliasId, body) => request('put', '/station-aliases/' + aliasId, { data: body }),
    deleteStationAlias: (aliasId) => request('delete', '/station-aliases/' + aliasId),

    // Settings
    tolerance: () => request('get', '/settings/tolerance'),
    saveTolerance: (body) => request('put', '/settings/tolerance', { data: body }),
    episodePatterns: () => request('get', '/episode-patterns'),
    createEpisodePattern: (body) => request('post', '/episode-patterns', { data: body }),
    updateEpisodePattern: (patternId, body) => request('put', '/episode-patterns/' + patternId, { data: body }),
    deleteEpisodePattern: (patternId) => request('delete', '/episode-patterns/' + patternId),
    testEpisodePattern: (body) => request('post', '/episode-patterns/test', { data: body }),
    rederiveEpisodes: (id, dryRun) => request('post', '/' + id + '/episodes/rederive', { data: { dryRun } }),

    // Background jobs: the shared progress endpoint
    progress: (processId) => axios.get(api.rootUrl + 'progress/' + processId, api.getConfig())
        .then(response => response.data)
        .catch(() => null),

    // Mapping templates of a domain, as the Mappings screen lists them
    mappings: (direction, domain) => api.getListData('account/mappings', '1', 'direction=' + direction + '&domain=' + domain + '&maintainer=self%2Balv')
}
